
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DailySnapshotParams struct
 */
class DailySnapshotParams {
  /**
   * Initialize struct
   * @param {!number=} accountLogin
   * @param {!Date=} rolloverTimestamp
   * @constructor
   */
  constructor (accountLogin = new UInt64(0, 0), rolloverTimestamp = new Date(0)) {
    this.AccountLogin = accountLogin
    this.RolloverTimestamp = rolloverTimestamp
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DailySnapshotParams}
   * @param {!DailySnapshotParams} other Other struct
   * @returns {!DailySnapshotParams} This struct
   */
  copy (other) {
    if (other.AccountLogin != null) {
      this.AccountLogin = UInt64.fromNumber(other.AccountLogin)
    } else {
      this.AccountLogin = null
    }
    if (other.RolloverTimestamp != null) {
      if (other.RolloverTimestamp instanceof Date) {
        this.RolloverTimestamp = new Date(other.RolloverTimestamp.getTime())
      } else {
        this.RolloverTimestamp = new Date(Math.round(other.RolloverTimestamp / 1000000))
      }
    } else {
      this.RolloverTimestamp = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DailySnapshotParams}
   * @returns {!DailySnapshotParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DailySnapshotParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DailySnapshotParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DailySnapshotParams}
   * @param {!DailySnapshotParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DailySnapshotParams)) {
      throw new TypeError('Instance of DailySnapshotParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DailySnapshotParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      AccountLogin: ((this.AccountLogin != null) ? this.AccountLogin.toNumber() : null),
      RolloverTimestamp: ((this.RolloverTimestamp != null) ? (this.RolloverTimestamp.getTime() * 1000000) : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DailySnapshotParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DailySnapshotParams} other Object value
   * @returns {!DailySnapshotParams} Created struct
   */
  static fromObject (other) {
    return new DailySnapshotParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DailySnapshotParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DailySnapshotParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DailySnapshotParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10046
  }
}

export { DailySnapshotParams };

/**
 * Fast Binary Encoding DailySnapshotParams field model
 */
class FieldModelDailySnapshotParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.AccountLogin = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.RolloverTimestamp = new fbe.FieldModelTimestamp(buffer, this.AccountLogin.fbeOffset + this.AccountLogin.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDailySnapshotParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDailySnapshotParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.AccountLogin.fbeSize + this.RolloverTimestamp.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDailySnapshotParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.AccountLogin.fbeExtra + this.RolloverTimestamp.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDailySnapshotParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDailySnapshotParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDailySnapshotParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10046
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDailySnapshotParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDailySnapshotParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountLogin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountLogin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountLogin.fbeSize

    if ((fbeCurrentSize + this.RolloverTimestamp.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.RolloverTimestamp.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RolloverTimestamp.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDailySnapshotParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDailySnapshotParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDailySnapshotParams}
   * @param {!DailySnapshotParams} fbeValue Default value, defaults is new DailySnapshotParams()
   * @returns {!DailySnapshotParams} DailySnapshotParams value
   */
  get (fbeValue = new DailySnapshotParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDailySnapshotParams}
   * @param {!DailySnapshotParams} fbeValue DailySnapshotParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountLogin.fbeSize) <= fbeStructSize) {
      fbeValue.AccountLogin = this.AccountLogin.get()
    } else {
      fbeValue.AccountLogin = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountLogin.fbeSize

    if ((fbeCurrentSize + this.RolloverTimestamp.fbeSize) <= fbeStructSize) {
      fbeValue.RolloverTimestamp = this.RolloverTimestamp.get()
    } else {
      fbeValue.RolloverTimestamp = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.RolloverTimestamp.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDailySnapshotParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDailySnapshotParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDailySnapshotParams}
   * @param {!DailySnapshotParams} fbeValue DailySnapshotParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDailySnapshotParams}
   * @param {DailySnapshotParams} fbeValue DailySnapshotParams value
   */
  setFields (fbeValue) {
    this.AccountLogin.set(fbeValue.AccountLogin)
    this.RolloverTimestamp.set(fbeValue.RolloverTimestamp)
  }
}

export { FieldModelDailySnapshotParams };

/**
 * Fast Binary Encoding DailySnapshotParams model
 */
class DailySnapshotParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDailySnapshotParams(this.buffer, 4)
  }

  /**
   * Get the DailySnapshotParams model
   * @this {!DailySnapshotParamsModel}
   * @returns {!FieldModelDailySnapshotParams} model DailySnapshotParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DailySnapshotParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DailySnapshotParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DailySnapshotParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DailySnapshotParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDailySnapshotParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DailySnapshotParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DailySnapshotParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DailySnapshotParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DailySnapshotParamsModel}
   * @param {!DailySnapshotParams} value DailySnapshotParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DailySnapshotParamsModel}
   * @param {!DailySnapshotParams} value DailySnapshotParams value, defaults is new DailySnapshotParams()
   * @return {!object} Deserialized DailySnapshotParams value and its size
   */
  deserialize (value = new DailySnapshotParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DailySnapshotParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DailySnapshotParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DailySnapshotParamsModel}
   * @param {!number} prev Previous DailySnapshotParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DailySnapshotParamsModel };
